import * as React from 'react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminRemarksDialog from './components/AdminRemarksDialog';
import {
    CssBaseline,
    Box,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    Button,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextareaAutosize,
    TableSortLabel,
    Snackbar
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';

const StyledBox = styled(Box)({
    padding: '30px',
    fontFamily: '"Roboto", sans-serif',
    position: 'relative',
    minHeight: '100vh',
    backgroundColor: '#fff',
});

const FormRow = styled(Box)({
    display: 'flex',
    gap: '20px',
    alignItems: 'center',
    flexWrap: 'wrap',
    marginBottom: '30px',
});

const StyledButton = styled(Button)({
    height: '40px',
    backgroundColor: '#1976d2',
    color: '#fff',
    '&:hover': {
        backgroundColor: '#1565c0',
    },
});

const LogoutButton = styled(Button)({
    position: 'absolute',
    top: '20px',
    right: '30px',
    backgroundColor: 'transparent',
    color: '#444',
    boxShadow: 'none',
    '&:hover': {
        backgroundColor: 'rgba(0, 0, 0, 0.04)',
    },
});

const ErrorMessage = styled(Typography)({
    color: 'red',
    marginBottom: '10px',
});

const ExportButton = styled(Button)({
    marginTop: '20px',
});

// Sorting utilities
function descendingComparator(a, b, orderBy) {
    if (b[orderBy] < a[orderBy]) return -1;
    if (b[orderBy] > a[orderBy]) return 1;
    return 0;
}

function getComparator(order, orderBy) {
    return order === 'desc'
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
    const stabilized = array.map((el, index) => [el, index]);
    stabilized.sort((a, b) => {
        const cmp = comparator(a[0], b[0]);
        if (cmp !== 0) return cmp;
        return a[1] - b[1];
    });
    return stabilized.map(el => el[0]);
}

export default function EnquiryFeedback() {
    const todayd = new Date();
    const weekAgo = new Date();
    weekAgo.setDate(todayd.getDate() - 7);

    const navigate = useNavigate();
    const [type, setType] = useState('enquiry');
    const [fromDate, setFromDate] = useState(weekAgo);
    const [toDate, setToDate] = useState(todayd);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [records, setRecords] = useState([]);
    const [showExport, setShowExport] = useState(false);

    // Sorting state
    const [order, setOrder] = useState('desc');
    const [orderBy, setOrderBy] = useState('date');
    const [dialogOpen, setDialogOpen] = useState(false);
    // const [selectedRecordId, setSelectedRecordId] = useState(null);
    const [newStatus, setNewStatus] = useState('');
    // const [adminRemarkInput, setAdminRemarkInput] = useState('');
    const [selectedRecord, setSelectedRecord] = useState(null);
    // const [dialogSubmitting, setDialogSubmitting] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMsg, setSnackbarMsg] = useState('');

    // today's date for validation
    const today = new Date();

    // handlers enforcing From ≤ To ≤ Today
    const handleFromDateChange = (newValue) => {
        setError('');
        if (toDate && newValue > toDate) {
            setError('From Date must be on or before To Date');
            return;
        }
        setFromDate(newValue);
    };

    const handleToDateChange = (newValue) => {
        setError('');
        if (fromDate && newValue < fromDate) {
            setError('To Date must be on or after From Date');
            return;
        }
        if (newValue > today) {
            setError("To Date can't be in the future");
            return;
        }
        setToDate(newValue);
    };

    const formatDate = date => {
        if (!date) return null;
        const d = new Date(date);
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    };

    const handleSubmit = async () => {
        // block submit if there's a date error
        if (error) return;

        setError('');
        const schoolid = localStorage.getItem('schoolid') || sessionStorage.getItem('schoolid');
        if (!schoolid) {
            setError('Login information not found.');
            return;
        }
        if (!fromDate || !toDate) {
            setError('Please select both dates.');
            return;
        }

        setLoading(true);
        setShowExport(false);

        try {
            const endpoint = type === 'feedback' ? '/get_feedbacks' : '/get_enquiries';
            const res = await fetch(`https://chat.visualbench.com${endpoint}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    schoolid,
                    fromdate: formatDate(fromDate),
                    todate: formatDate(toDate)
                }),
            });
            const data = await res.json();
            if (!Array.isArray(data)) {
                setError(data.error || 'Unexpected error.');
                setRecords([]);
                return;
            }
            if (data.length === 0) {
                setError('No records found.');
                setRecords([]);
                return;
            }

            const mapped = data.map(item => {
                const dt = new Date(item.timestamp);
                const options = {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: true,
                };
                const formattedDate = dt.toLocaleString('en-GB', options).replace(',', '');



                const dt1 = new Date(item.adminupdate_time);
                const formattedDate1 = dt1.toLocaleString('en-GB', options).replace(',', '');

                return {
                    id: type === 'enquiry' ? item.enquiry_id : item.feedback_id,
                    name: item.name || '',
                    contact: item.contact || '',
                    email: item.email || '',
                    remarks: type === 'enquiry' ? (item.remarks || '') : (item.feedback || ''),
                    stream: item.stream || '',
                    date: formattedDate,
                    adminupdate:item.adminupdate_time ? formattedDate1 : null,
                    status: item.status || 'P',
                    adminRemarks: item.adminremarks || ''
                };
            });

            setRecords(mapped);
            setShowExport(true);
        } catch (err) {
            console.error(err);
            setError('Error fetching data.');
            setRecords([]);
        } finally {
            setLoading(false);
        }
    };

    // const handleStatusChange = (id, newStatus) => {
    //     setRecords(prev => prev.map(r => r.id === id ? { ...r, status: newStatus } : r));

    //     // TODO: Persist change via API
    // };
    const handleStatusChange = async (id, status) => {
        if (status === 'edit') {
            const record = records.find(r => r.id === id);
            setNewStatus(record.status); // Keep status as Addressed
            setSelectedRecord(record || null);
            setDialogOpen(true);
            return;
        }
        else if (status === 'P') {
            const record = records.find(r => r.id === id);
            setNewStatus('P'); // Keep status as Addressed
            setSelectedRecord(record || null);

            try {
                setError('');

                const schoolid = localStorage.getItem('schoolid') || sessionStorage.getItem('schoolid');
                if (!schoolid) throw new Error('Login information not found.');

                const response = await fetch('https://chat.visualbench.com/updateRecord', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        type,
                        selectedid: record?.id,
                        status,
                        adminremarks: ''
                    })
                });

                const result = await response.json();

                if (!response.ok) throw new Error(result.error || 'Failed to update record.');

                // Update UI after server confirms
                const updated = records.map(r =>
                    r.id === id ? { ...r, status, adminRemarks: '' } : r
                );
                setRecords(updated);

                setSnackbarMsg('Record updated successfully.');
                setSnackbarOpen(true);
            } catch (err) {
                console.error(err);
                setError(err.message || 'Failed to update record.');
            }
            return;

        }
        else {
            const record = records.find(r => r.id === id);
            setNewStatus(status);
            //setAdminRemarkInput(record?.adminRemarks || '');
            setSelectedRecord(record || null);
            setDialogOpen(true);
        }
    };

    const handleRequestSort = (event, property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleExport = () => {
        if (!records.length) return;
        const headers = type === 'enquiry'
            ? ['Name', 'Contact', 'Email', 'User Remarks', 'Stream', 'Date', 'Status', 'Remarks']
            : ['Name', 'Contact', 'Email', 'Feedback', 'Date', 'Status', 'Remarks'];
        const rows = records.map(r => [
            r.name, r.contact, r.email, r.remarks, ...(type === 'enquiry' ? [r.stream] : []), r.date, r.status, r.adminRemarks
        ]);
        const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = 'records.csv';
        a.click();
    };

    const handleLogout = () => {
        localStorage.removeItem('schoolid');
        sessionStorage.removeItem('schoolid');
        localStorage.removeItem('password');
        sessionStorage.removeItem('password');
        navigate('/');
    };

    useEffect(() => {
        const school = localStorage.getItem('schoolid') || sessionStorage.getItem('schoolid');
        const pwd = localStorage.getItem('password') || sessionStorage.getItem('password');
        if (!school || !pwd) navigate('/');
    }, [navigate]);

    const headCells = [
        { id: 'name', label: 'Name' },
        { id: 'contact', label: 'Contact' },
        { id: 'email', label: 'Email' },
        { id: 'remarks', label: type === 'enquiry' ? 'User Remarks' : 'Feedback' },
        ...(type === 'enquiry' ? [{ id: 'stream', label: 'Stream' }] : []),
        { id: 'date', label: 'Date' },
        { id: 'status', label: 'Status' },
        { id: 'adminRemarks', label: 'Remarks' }
    ];

    return (
        <>
            <CssBaseline />
            <StyledBox>
                <LogoutButton onClick={handleLogout}>Logout</LogoutButton>

                <Typography variant="h5" sx={{ mb: 2 }}>Filter Records</Typography>
                <FormRow>
                    <FormControl sx={{ minWidth: 150 }}>
                        <InputLabel>Type</InputLabel>
                        <Select
                            value={type}
                            onChange={e => { setType(e.target.value); setRecords([]); setShowExport(false); }}
                            label="Type"
                        >
                            <MenuItem value="enquiry">Enquiry</MenuItem>
                            <MenuItem value="feedback">Feedback</MenuItem>
                        </Select>
                    </FormControl>

                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                            label="From Date"
                            value={fromDate}
                            format="dd-MM-yyyy"
                            onChange={handleFromDateChange}
                            renderInput={params => <TextField {...params} />}
                        />
                        <DatePicker
                            label="To Date"
                            value={toDate}
                            format="dd-MM-yyyy"
                            onChange={handleToDateChange}
                            renderInput={params => <TextField {...params} />}
                        />
                    </LocalizationProvider>

                    <Box sx={{ minWidth: 120 }}>
                        {loading
                            ? <CircularProgress size={22} sx={{ color: '#626cd6', mt: 2 }} />
                            : <StyledButton onClick={handleSubmit}>Submit</StyledButton>
                        }
                    </Box>
                </FormRow>

                {error && <ErrorMessage>{error}</ErrorMessage>}

                {/* <ExportButton
                    variant="contained"
                    onClick={handleExport}
                    sx={{ display: showExport ? 'inline-block' : 'none' }}
                >
                    Export to CSV
                </ExportButton> */}

                {records.length > 0 && (
                    <TableContainer component={Paper} sx={{ mt: 3 }}>
                        <Table size="small" sx={{ minWidth: 650 }}>
                            <TableHead>
                                <TableRow>
                                    {headCells.map(headCell => (
                                        <TableCell
                                            key={headCell.id}
                                            sortDirection={orderBy === headCell.id ? order : false}
                                        >
                                            <TableSortLabel
                                                active={orderBy === headCell.id}
                                                direction={orderBy === headCell.id ? order : 'asc'}
                                                onClick={event => handleRequestSort(event, headCell.id)}
                                            >
                                                {headCell.label}
                                            </TableSortLabel>
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {stableSort(records, getComparator(order, orderBy)).map(row => (
                                    <TableRow
                                        key={row.id}
                                        sx={{
                                            '&:nth-of-type(odd)': { backgroundColor: '#f5f5f5' },
                                            '&:hover': { backgroundColor: '#e0e0e0' },
                                            '& td, & th': { padding: '8px 16px' }
                                        }}
                                    >
                                        <TableCell>{row.name}</TableCell>
                                        <TableCell>{row.contact}</TableCell>
                                        <TableCell>{row.email}</TableCell>
                                        <TableCell>{row.remarks}</TableCell>
                                        {type === 'enquiry' && <TableCell>{row.stream}</TableCell>}
                                        <TableCell>{row.date}</TableCell>
                                        <TableCell>
                                            <FormControl fullWidth size="small">
                                                <Select
                                                    value={row.status}
                                                    onChange={e => handleStatusChange(row.id, e.target.value)}
                                                    sx={{ minHeight: 0, paddingY: 0 }}
                                                >
                                                    <MenuItem value="P">Pending</MenuItem>
                                                    <MenuItem value="A">Addressed</MenuItem>
                                                    {row.status !== 'P' && (
                                                        <MenuItem value="edit">Edit</MenuItem>
                                                    )}
                                                </Select>
                                            </FormControl>
                                        </TableCell>
                                        <TableCell>{row.adminRemarks}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
                <AdminRemarksDialog
                    open={dialogOpen}
                    onClose={() => setDialogOpen(false)}
                    record={selectedRecord}
                    status={newStatus}
                    type={type}
                    onSubmit={(id, status, remarks) => {
                        setRecords(prev =>
                            prev.map(r => r.id === id
                                ? { ...r, status, adminRemarks: remarks }
                                : r)
                        );
                        setSnackbarMsg('Record updated successfully.');
                        setSnackbarOpen(true);
                    }}
                />
            </StyledBox>
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={3000}
                onClose={() => setSnackbarOpen(false)}
                message={snackbarMsg}
            />
        </>
    );
}